import { type Selection, build } from '@noths/polaris-product-listings-urls';

import { getActiveOptionValues } from 'src/redux/productsQuery/helpers/facetOptionHelpers';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { TaxonomyAttribute } from 'src/redux/productsQuery/types/ProductsQueryState';
import { shopSurfaceBasePath } from 'src/utils/shopSurfaceBasePath';

/**
 * The taxonomy `Selection[]` formed by the active options of the first
 * `levelCount` taxonomy levels. Levels with no active option are skipped.
 */
export const getActiveTaxonomySelections = (
  facets: Facet[],
  taxonomyAttributeAliases: TaxonomyAttribute[],
  levelCount: number,
): Selection[] =>
  taxonomyAttributeAliases.slice(0, levelCount).flatMap((alias) => {
    const values = getActiveOptionValues(facets.find((facet) => facet.alias === alias));

    return values.length > 0 ? [{ alias, values }] : [];
  });

/**
 * Build the canonical taxonomy URL for the page defined by the active options of
 * the first `levelCount` levels, optionally appending a single `leaf` selection
 * (e.g. a child option the user can navigate into).
 */
export const buildTaxonomyHref = ({
  facets,
  leaf,
  levelCount,
  surface,
  taxonomyAttributeAliases,
}: {
  facets: Facet[];
  leaf?: Selection;
  levelCount: number;
  surface?: string;
  taxonomyAttributeAliases: TaxonomyAttribute[];
}): string => {
  const ancestors = getActiveTaxonomySelections(facets, taxonomyAttributeAliases, levelCount);

  return build({
    basePath: shopSurfaceBasePath(surface) ?? '',
    filters: leaf ? [...ancestors, leaf] : ancestors,
    taxonomyAliases: taxonomyAttributeAliases,
  });
};
